import { useCallback, useMemo, useSyncExternalStore } from 'react'

const EMPTY_CONFIG = Object.freeze({ hiddenColumns: null, columnOrder: [], pinnedColumns: null })

const configs = new Map()
const listeners = new Set()

function subscribe(listener) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function notify() {
  listeners.forEach(listener => listener())
}

function getConfig(gridKey) {
  return configs.get(gridKey) ?? EMPTY_CONFIG
}

function updateConfig(gridKey, patch) {
  if (!gridKey) {
    return
  }

  configs.set(gridKey, { ...getConfig(gridKey), ...patch })
  notify()
}

function clearConfig(gridKey) {
  if (!configs.has(gridKey)) {
    return
  }

  configs.delete(gridKey)
  notify()
}

export function clearAllGridColumnsConfig() {
  configs.clear()
  notify()
}

export function useGridColumnsConfig(gridKey) {
  const config = useSyncExternalStore(subscribe, () => getConfig(gridKey))

  const setHiddenColumns = useCallback(hiddenColumns => updateConfig(gridKey, { hiddenColumns }), [gridKey])
  const setColumnOrder = useCallback(columnOrder => updateConfig(gridKey, { columnOrder }), [gridKey])
  const setPinnedColumns = useCallback(pinnedColumns => updateConfig(gridKey, { pinnedColumns }), [gridKey])
  const resetColumnsConfig = useCallback(() => clearConfig(gridKey), [gridKey])

  return useMemo(() => ({
    ...config,
    setHiddenColumns,
    setColumnOrder,
    setPinnedColumns,
    resetColumnsConfig,
  }), [config, setHiddenColumns, setColumnOrder, setPinnedColumns, resetColumnsConfig])
}
